import { useState } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function DynamicForm({ fields, onSubmit, submitLabel, loading, testid }) {
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const enabled = (fields || []).filter((f) => f.enabled !== false);
  const set = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const pickFile = (f) => (e) => {
    const sel = e.target.files?.[0];
    if (!sel) return;
    const ext = sel.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) { toast.error("Only PDF, DOC or DOCX allowed"); return; }
    if (sel.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setFiles((s) => ({ ...s, [f.name]: sel }));
  };

  const submit = (e) => {
    e.preventDefault();
    for (const f of enabled) {
      if (!f.required) continue;
      if (f.type === "file") { if (!files[f.name]) { toast.error(`${f.label} is required`); return; } }
      else if (!(values[f.name] || "").trim()) { toast.error(`${f.label} is required`); return; }
    }
    onSubmit(values, files);
  };

  return (
    <form onSubmit={submit} data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
      {enabled.map((f) => {
        const col = f.width === "full" ? "sm:col-span-2" : "";
        const req = f.required ? " *" : "";
        const ml = f.maxlength > 0 ? { maxLength: f.maxlength } : {};
        if (f.type === "file") return (
          <div key={f.id} className={col}>
            <Label>{f.label}{req}</Label>
            <label data-testid={`field-${f.name}`} className="mt-1.5 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-8 cursor-pointer hover:border-emerald-brand transition-colors">
              <UploadCloud className="text-emerald-brand" size={26} />
              <span className="text-sm text-slate-600">{files[f.name]?.name || "Click to upload"}</span>
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={pickFile(f)} />
            </label>
          </div>
        );
        if (f.type === "textarea") return (
          <div key={f.id} className={col}><Label>{f.label}{req}</Label><Textarea data-testid={`field-${f.name}`} className="mt-1.5 focus-visible:ring-emerald-brand" rows={4} placeholder={f.placeholder} {...ml} value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} /></div>
        );
        if (f.type === "select") return (
          <div key={f.id} className={col}><Label>{f.label}{req}</Label>
            <Select value={values[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
              <SelectTrigger className="mt-1.5" data-testid={`field-${f.name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select></div>
        );
        return (
          <div key={f.id} className={col}><Label>{f.label}{req}</Label><Input type={f.type || "text"} data-testid={`field-${f.name}`} className="mt-1.5 focus-visible:ring-emerald-brand" placeholder={f.placeholder} {...ml} value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} /></div>
        );
      })}
      <div className="sm:col-span-2"><Button type="submit" disabled={loading} data-testid={`${testid}-submit`} className="w-full bg-emerald-brand hover:bg-emerald-dark rounded-full py-6 text-base font-semibold">{loading ? "Submitting…" : submitLabel}</Button></div>
    </form>
  );
}
